import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import type { EventPopup } from "../../lib/supabase";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";

const EventsPage = () => {
  const [events, setEvents] = useState<EventPopup[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState<EventPopup | null>(null);
  const [registering, setRegistering] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      const { data } = await supabase
        .from("event_popups")
        .select("*")
        .order("created_at", { ascending: false });
      setEvents((data as EventPopup[]) ?? []);
      setLoading(false);
    };
    fetchEvents();

    const channel = supabase
      .channel("events_page_realtime")
      .on("postgres_changes", { event: "*", schema: "public", table: "event_popups" }, () => {
        fetchEvents();
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, []);

  const activeEvents = events.filter((e) => e.is_active);
  const pastEvents = events.filter((e) => !e.is_active);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const form = e.currentTarget;
    const formData = new URLSearchParams();
    const elements = form.elements;
    for (let i = 0; i < elements.length; i++) {
      const el = elements[i] as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
      if (el.name && el.value) formData.append(el.name, el.value);
    }
    if (selectedEvent?.title_vi) formData.append("event_name", selectedEvent.title_vi);
    try {
      await fetch("https://readdy.ai/api/form/d7bltbiqofe8vli40kq0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formData.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Navbar />

      {/* Hero */}
      <section className="bg-gradient-to-r from-[#004A99] to-[#003d7d] text-white py-16">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="flex items-center gap-2 mb-4 text-sm">
            <Link to="/" className="text-white/60 hover:text-white cursor-pointer">Trang chủ</Link>
            <i className="ri-arrow-right-s-line text-white/40" />
            <span className="text-white/80">Sự Kiện</span>
          </div>
          <h1 className="text-3xl font-black mb-3">Sự Kiện &amp; Hoạt Động</h1>
          <p className="text-white/70 text-sm max-w-xl leading-relaxed">
            Cập nhật các sự kiện, hội thảo và hoạt động kết nối doanh nghiệp Hàn–Việt mới nhất
          </p>
          <div className="mt-6 flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2 bg-white/10 rounded-xl px-4 py-2">
              <i className="ri-calendar-event-line text-white" />
              <span className="text-sm font-semibold">{activeEvents.length} Sự kiện đang diễn ra</span>
            </div>
            <div className="flex items-center gap-2 bg-white/10 rounded-xl px-4 py-2">
              <i className="ri-history-line text-white" />
              <span className="text-sm font-semibold">{pastEvents.length} Sự kiện đã qua</span>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-[1200px] mx-auto px-6 py-12">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <i className="ri-loader-4-line animate-spin text-3xl text-[#004A99]" />
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl border border-gray-200">
            <i className="ri-calendar-event-line text-5xl text-gray-200 block mb-4" />
            <p className="text-gray-500 font-semibold">Chưa có sự kiện nào được đăng</p>
            <p className="text-gray-400 text-sm mt-1">Vui lòng quay lại sau để xem các sự kiện mới nhất</p>
          </div>
        ) : (
          <>
            {/* Active Events */}
            {activeEvents.length > 0 && (
              <div className="mb-12">
                <div className="flex items-center gap-2 mb-6">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                  <h2 className="text-xl font-bold text-gray-800">Sự Kiện Đang Diễn Ra</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {activeEvents.map((event) => (
                    <EventCard key={event.id} event={event} onRegister={() => { setSelectedEvent(event); setRegistering(true); setSubmitted(false); }} />
                  ))}
                </div>
              </div>
            )}

            {/* Past Events */}
            {pastEvents.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-6">
                  <i className="ri-history-line text-gray-400" />
                  <h2 className="text-xl font-bold text-gray-800">Sự Kiện Đã Qua</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 opacity-75">
                  {pastEvents.map((event) => (
                    <EventCard key={event.id} event={event} isPast />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Registration Modal */}
      {registering && selectedEvent && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between z-10">
              <div>
                <h2 className="font-bold text-gray-800 text-sm">Đăng Ký Tham Gia</h2>
                <p className="text-xs text-gray-500 mt-0.5 truncate max-w-[260px]">{selectedEvent.title_vi}</p>
              </div>
              <button onClick={() => setRegistering(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-gray-500" />
              </button>
            </div>
            <div className="p-6">
              {submitted ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                    <i className="ri-checkbox-circle-fill text-4xl text-green-500" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-800 mb-2">Đăng ký thành công!</h3>
                  <p className="text-sm text-gray-500 mb-5">Chúng tôi sẽ liên hệ với bạn trong thời gian sớm nhất.</p>
                  <button onClick={() => setRegistering(false)} className="px-6 py-2.5 bg-[#004A99] text-white rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap hover:bg-[#003a7a]">
                    Đóng
                  </button>
                </div>
              ) : (
                <form id="event-registration-form" data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Họ và tên *</label>
                    <input type="text" name="name" required placeholder="Nguyễn Văn A" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#004A99] bg-[#F8F9FA]" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Email *</label>
                    <input type="email" name="email" required placeholder="email@company.com" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#004A99] bg-[#F8F9FA]" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Số điện thoại</label>
                    <input type="tel" name="phone" placeholder="0901 000 000" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#004A99] bg-[#F8F9FA]" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Nội dung thắc mắc</label>
                    <textarea name="message" rows={3} maxLength={500} placeholder="Nhập nội dung thắc mắc... (tối đa 500 ký tự)" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#004A99] resize-none bg-[#F8F9FA]" />
                  </div>
                  <button type="submit" disabled={submitting} className="w-full py-3 bg-[#004A99] hover:bg-[#003a7a] text-white font-bold rounded-xl text-sm cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center justify-center gap-2 transition-colors">
                    {submitting ? <><i className="ri-loader-4-line animate-spin" />Đang gửi...</> : <><i className="ri-send-plane-line" />Gửi Đăng Ký</>}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

interface EventCardProps {
  event: EventPopup;
  isPast?: boolean;
  onRegister?: () => void;
}

const EventCard = ({ event, isPast, onRegister }: EventCardProps) => {
  const bgColor = event.bg_color ?? "#004A99";
  const highlights = (event.highlights ?? []) as string[];

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden flex flex-col hover:border-[#004A99]/30 transition-all group">
      {/* Image / Color header — click to detail */}
      <Link
        to={`/su-kien/${event.id}`}
        className="relative h-44 overflow-hidden flex-shrink-0 block cursor-pointer"
        style={{ backgroundColor: bgColor }}
      >
        {event.image_url ? (
          <img
            src={event.image_url}
            alt={event.title_vi}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <i className="ri-calendar-event-fill text-white/30 text-5xl" />
          </div>
        )}
        {!isPast && (
          <div className="absolute top-3 left-3">
            <span className="flex items-center gap-1 bg-green-500 text-white text-[10px] px-2.5 py-1 rounded-full font-bold">
              <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
              Đang diễn ra
            </span>
          </div>
        )}
        {isPast && (
          <div className="absolute top-3 left-3">
            <span className="bg-gray-500/80 text-white text-[10px] px-2.5 py-1 rounded-full font-bold">Đã kết thúc</span>
          </div>
        )}
        {/* View detail overlay */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
          <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-white text-[#004A99] text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1">
            <i className="ri-eye-line" /> Xem chi tiết
          </span>
        </div>
      </Link>

      {/* Content */}
      <div className="p-5 flex flex-col flex-1">
        <Link to={`/su-kien/${event.id}`} className="cursor-pointer">
          <h3 className="font-bold text-gray-900 text-sm leading-tight mb-2 line-clamp-2 group-hover:text-[#004A99] transition-colors">{event.title_vi}</h3>
        </Link>

        {/* Description with rich text */}
        {event.description_vi && (
          <div
            className="text-xs text-gray-500 mb-3 line-clamp-3 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: event.description_vi }}
          />
        )}

        {/* Meta */}
        <div className="space-y-1.5 mb-4">
          {event.event_date && (
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <i className="ri-calendar-line text-[#004A99] flex-shrink-0" />
              <span>{event.event_date}</span>
            </div>
          )}
          {event.event_location_vi && (
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <i className="ri-map-pin-line text-[#004A99] flex-shrink-0" />
              <span className="line-clamp-1">{event.event_location_vi}</span>
            </div>
          )}
        </div>

        {/* Highlights */}
        {highlights.length > 0 && (
          <div className="bg-[#F0F6FF] rounded-xl p-3 mb-4">
            <ul className="space-y-1">
              {highlights.slice(0, 3).map((h, i) => (
                <li key={i} className="flex items-start gap-1.5 text-xs text-gray-700">
                  <i className="ri-checkbox-circle-fill text-[#004A99] flex-shrink-0 mt-0.5 text-xs" />
                  <span>{h}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Actions */}
        <div className="mt-auto flex gap-2">
          <Link
            to={`/su-kien/${event.id}`}
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 border-2 text-xs font-semibold rounded-xl cursor-pointer whitespace-nowrap transition-colors hover:opacity-80"
            style={{ borderColor: bgColor, color: bgColor }}
          >
            <i className="ri-article-line" />
            Xem đầy đủ thông tin
          </Link>
          {!isPast && onRegister && (
            <button
              onClick={onRegister}
              className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-white text-xs font-semibold rounded-xl cursor-pointer whitespace-nowrap transition-colors hover:opacity-90"
              style={{ backgroundColor: bgColor }}
            >
              <i className="ri-user-add-line" />
              Đăng Ký
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventsPage;
